import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonWidgetVariants = cva(
  "dsw-inline-flex dsw-items-center dsw-justify-center dsw-whitespace-nowrap dsw-text-sm dsw-font-medium dsw-ring-offset-background dsw-transition-colors dsw-focus-visible:dsw-outline-none dsw-focus-visible:dsw-ring-2 dsw-focus-visible:dsw-ring-ring dsw-focus-visible:dsw-ring-offset-2 dsw-disabled:dsw-pointer-events-none dsw-disabled:dsw-opacity-50 dsw-cursor-pointer",
  {
    variants: {
      variant: {
        default: "dsw-bg-white dsw-text-black dsw-border dsw-border-gray-200 hover:dsw-bg-black hover:dsw-text-white",
        destructive:
          "dsw-bg-destructive dsw-text-destructive-foreground hover:dsw-bg-destructive/90",
        outline:
          "dsw-border dsw-border-input dsw-bg-background hover:dsw-bg-accent hover:dsw-text-accent-foreground",
        secondary:
          "dsw-bg-secondary dsw-text-secondary-foreground hover:dsw-bg-secondary/80",
        ghost: "hover:dsw-bg-accent hover:dsw-text-accent-foreground",
        link: "dsw-text-primary dsw-underline-offset-4 hover:dsw-underline",
        success: "dsw-bg-green-600 dsw-text-white hover:dsw-bg-green-700",
        warning: "dsw-bg-yellow-600 dsw-text-white hover:dsw-bg-yellow-700",
        info: "dsw-bg-blue-600 dsw-text-white hover:dsw-bg-blue-700",
        gradient: "dsw-bg-gradient-to-r dsw-from-[#f36744] dsw-to-[#8e57fb] dsw-text-white hover:dsw-from-[#e55a3a] hover:dsw-to-[#7c4dd8]",
      },
      size: {
        default: "dsw-h-10 dsw-px-4 dsw-py-2",
        sm: "dsw-h-9 dsw-px-3",
        lg: "dsw-h-11 dsw-px-8",
        icon: "dsw-h-10 dsw-w-10",
      },
      radius: {
        default: "dsw-rounded-lg",
        sm: "dsw-rounded-sm",
        md: "dsw-rounded-md",
        lg: "dsw-rounded-lg",
        xl: "dsw-rounded-xl",
        full: "dsw-rounded-full",
        none: "dsw-rounded-none",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      radius: "default",
    },
  }
)

export interface ButtonWidgetProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonWidgetVariants> {
  asChild?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  bgColor?: string
  textColor?: string
  hoverBgColor?: string
  hoverTextColor?: string
  gradient?: string
  hoverGradient?: string
}

const ButtonWidget = React.forwardRef<HTMLButtonElement, ButtonWidgetProps>(
  ({ 
    className, 
    variant, 
    size, 
    radius, 
    asChild = false, 
    leftIcon, 
    rightIcon, 
    children, 
    bgColor,
    textColor,
    hoverBgColor,
    hoverTextColor,
    gradient,
    hoverGradient,
    style,
    ...props 
  }, ref) => {
    const isIconOnly = size === "icon" 
    // Determine if we should use gradient or solid color
    const hasGradient = gradient && gradient.includes('gradient')
    const hasHoverGradient = hoverGradient && hoverGradient.includes('gradient')
    
    // Build custom styles for colors and gradients
    const customStyles: React.CSSProperties = {
      ...style,
      ...(hasGradient && { background: gradient }),
      ...(!hasGradient && bgColor && { backgroundColor: bgColor }),
      ...(textColor && { color: textColor }),
    }
    
    // Build custom className for hover states
    const customHoverClasses = [
      hasHoverGradient && `hover:dsw-bg-[${hoverGradient}]`,
      !hasHoverGradient && hoverBgColor && `hover:dsw-bg-[${hoverBgColor}]`,
      hoverTextColor && `hover:dsw-text-[${hoverTextColor}]`,
    ].filter(Boolean).join(' ')
    
    const buttonClasses = cn(
      buttonWidgetVariants({ variant, size, radius, className }),
      // Only apply custom hover classes if custom colors are provided
      (hoverBgColor || hoverTextColor || hasHoverGradient) && customHoverClasses
    )

    if (asChild) {
      return (
        <Slot
          className={buttonClasses}
          style={customStyles}
          ref={ref}
          {...props}
        >
          {!isIconOnly && leftIcon && (
            <span className="dsw-mr-2 dsw-flex dsw-items-center">
              {leftIcon}
            </span>
          )}
          {isIconOnly && leftIcon && (
            <span className="dsw-flex dsw-items-center dsw-justify-center">
              {leftIcon}
            </span>
          )}
          {!isIconOnly && children}
          {!isIconOnly && rightIcon && (
            <span className="dsw-ml-2 dsw-flex dsw-items-center">
              {rightIcon}
            </span>
          )}
        </Slot>
      )
    }

    return (
      <button
        className={buttonClasses}
        style={customStyles}
        ref={ref}
        {...props}
      >
        {!isIconOnly && leftIcon && (
          <span className="dsw-mr-2 dsw-flex dsw-items-center">
            {leftIcon}
          </span>
        )}
        {isIconOnly && leftIcon && (
          <span className="dsw-flex dsw-items-center dsw-justify-center">
            {leftIcon}
          </span>
        )}
        {!isIconOnly && children}
        {!isIconOnly && rightIcon && (
          <span className="dsw-ml-2 dsw-flex dsw-items-center">
            {rightIcon}
          </span>
        )}
      </button>
    )
  }
)
ButtonWidget.displayName = "ButtonWidget"

export { ButtonWidget, buttonWidgetVariants } 